<template>
  <div class="aviris-tab">
    <p class="text-muted small mb-2">
      <i class="fas fa-satellite"></i>
      NASA AVIRIS-3 hyperspectral imagery — 284 reflectance bands from
      ~388–2493&nbsp;nm, where the disease-relevant spectral detail lives.
    </p>

    <!-- Needs farm geometry to know where to look for coverage. -->
    <div v-if="!farmBbox" class="alert alert-info small mb-0">
      Add block boundaries to this farm to discover AVIRIS-3 coverage.
    </div>

    <template v-else>
      <p v-if="loadingScenes" class="text-muted small mb-0">
        <i class="fas fa-spinner fa-spin"></i> Finding scenes over this farm…
      </p>
      <div v-else-if="scenesError" class="alert alert-warning small mb-0">
        {{ scenesError }}
      </div>

      <template v-else>
        <div v-if="!scenes.length" class="alert alert-info small mb-0">
          No AVIRIS-3 flights cover this farm yet. Coverage today is
          limited to a few regions (Finger Lakes, Lake Erie Grape Belt,
          Napa Valley).
        </div>

        <template v-else>
          <button
            type="button"
            class="btn btn-sm btn-block mb-2"
            :class="footprintsVisible ? 'btn-info' : 'btn-outline-info'"
            @click="toggleFootprints"
          >
            <i class="fas" :class="footprintsVisible ? 'fa-eye-slash' : 'fa-eye'"></i>
            {{ footprintsVisible ? 'Hide' : 'Show' }} coverage on map
          </button>

          <div class="aviris-section-label">
            {{ scenes.length }} scene{{ scenes.length === 1 ? '' : 's' }}
            cover this farm — pick one to sample
          </div>
          <div class="aviris-scene-list">
            <div
              v-for="s in scenes"
              :key="s.filename"
              class="aviris-scene"
              :class="{ 'is-active': activeScene && activeScene.filename === s.filename }"
              @click="selectScene(s)"
            >
              <div class="aviris-scene-main">
                <span class="aviris-scene-date">{{ sceneDate(s.filename) }}</span>
                <span v-if="sceneDetail(s)" class="aviris-scene-detail">
                  {{ sceneDetail(s) }}
                </span>
                <span class="aviris-scene-file">{{ s.filename }}</span>
              </div>
              <span
                class="aviris-badge"
                :class="{ 'is-ready': s.indexed }"
                :title="s.indexed
                  ? 'Indexed — point reads are fast'
                  : 'Not indexed yet — point reads fall back to a slow path'"
              >
                <i class="fas" :class="s.indexed ? 'fa-bolt' : 'fa-clock'"></i>
                {{ s.indexed ? 'Fast' : 'Slow' }}
              </span>
            </div>
          </div>

          <!-- RTK point dataset — browse the farm's folders, pick a point
               dataset, and click its points to sample the exact survey
               coordinate. The dataset renders with its own saved
               visualization settings (same as anywhere else in the app). -->
          <div class="aviris-dataset mt-2">
            <div class="aviris-section-label">Sample from a point dataset</div>

            <div class="aviris-crumbs">
              <span v-for="(crumb, i) in avBreadcrumbs" :key="crumb.id">
                <a class="aviris-crumb" @click="crumbTo(crumb, i)">{{ crumb.name }}</a>
                <span v-if="i + 1 < avBreadcrumbs.length" class="aviris-crumb-sep"> / </span>
              </span>
            </div>

            <p v-if="loadingFolder" class="text-muted small mb-0">
              <i class="fas fa-spinner fa-spin"></i> Loading…
            </p>
            <div v-else-if="folderError" class="aviris-sample-error">
              {{ folderError }}
            </div>
            <template v-else>
              <div class="aviris-browse">
                <div
                  v-for="f in avFolders"
                  :key="f.id"
                  class="aviris-browse-row"
                  @click="browseFolder(f)"
                >
                  <i class="fas fa-folder aviris-browse-icon"></i>
                  <span class="aviris-browse-name">{{ f.name }}</span>
                </div>
                <div
                  v-for="d in avDatasets"
                  :key="d.id"
                  class="aviris-browse-row aviris-browse-dataset"
                  :class="{ 'is-active': selectedDataset && selectedDataset.id === d.id }"
                  @click="selectDataset(d)"
                >
                  <i
                    class="fas aviris-browse-icon"
                    :class="d.dynamic
                      ? 'fa-satellite-dish'
                      : (d.file && d.file.raster ? 'fa-border-none' : 'fa-map-marker-alt')"
                  ></i>
                  <span class="aviris-browse-name">{{ d.name }}</span>
                </div>
                <p
                  v-if="!avFolders.length && !avDatasets.length"
                  class="aviris-hint mb-0"
                >This folder is empty.</p>
              </div>
            </template>

            <p v-if="launchingDataset" class="text-muted small mb-0 mt-1">
              <i class="fas fa-spinner fa-spin"></i> Loading dataset…
            </p>
            <p v-else-if="selectedDataset" class="aviris-hint">
              <strong>{{ selectedDataset.name }}</strong> loaded — click its
              points on the map to sample<template
                v-if="!activeScene"
              >, once a scene is selected above</template>.
            </p>
          </div>

          <!-- Sampling banner — live while a scene is selected. -->
          <div v-if="activeScene" class="aviris-sampling mt-2">
            <div class="aviris-sampling-head">
              <span>
                <i class="fas fa-crosshairs"></i>
                <template v-if="sampleLoading">Reading spectrum…</template>
                <template v-else>Click points on the map to sample</template>
              </span>
              <button
                type="button"
                class="btn btn-xs btn-outline-secondary"
                @click="stopSampling"
              >Done</button>
            </div>
            <div v-if="sampleError" class="aviris-sample-error">
              {{ sampleError }}
            </div>
          </div>

          <!-- Spectrum chart — one curve per sampled point. -->
          <div v-if="spectra.length" class="aviris-chart-panel mt-2">
            <div class="aviris-chart-head">
              <span>{{ spectra.length }} point{{ spectra.length === 1 ? '' : 's' }}</span>
              <button
                type="button"
                class="btn btn-xs btn-outline-secondary"
                @click="clearSpectra"
              ><i class="fas fa-times"></i> Clear</button>
            </div>
            <div class="aviris-chart-canvas">
              <canvas ref="spectrumCanvas"></canvas>
            </div>
            <div class="aviris-point-list">
              <div
                v-for="sp in spectra"
                :key="sp.label"
                class="aviris-point"
              >
                <span class="aviris-point-dot" :style="{ background: sp.color }"></span>
                <span class="aviris-point-label">{{ sp.label }}</span>
                <span class="aviris-point-coord">
                  {{ sp.lat.toFixed(5) }}, {{ sp.lon.toFixed(5) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-console */
import firebase from 'firebase/compat/app'

const turf = require('@turf/turf')
const fb = require('@/firebaseConfig.js')

// The AVIRIS-3 hyperspectral access service (Cloud Run). Mirrors the
// hard-coded endpoint pattern AnalysesTab.vue uses for runAnalysisRecipe.
const AVIRIS_BASE = 'https://aviris-service-231171307256.us-central1.run.app'

// Map source / layer ids — scene footprints and the markers dropped at
// each sampled location. RTK dataset points are rendered by the
// platform's own launchDataset action on its `datasetPreview` layer.
const FOOTPRINT_SRC = 'ev-aviris-footprints'
const FOOTPRINT_FILL = 'ev-aviris-footprints-fill'
const FOOTPRINT_GLOW = 'ev-aviris-footprints-glow'
const FOOTPRINT_LINE = 'ev-aviris-footprints-line'
const FOOTPRINT_LABEL = 'ev-aviris-footprints-label'
const SAMPLE_SRC = 'ev-aviris-samples'
const SAMPLE_LAYER = 'ev-aviris-samples-circ'
const DATASET_LAYER = 'datasetPreview' // the platform's point-dataset layer

// Curve / marker colours, cycled per sampled point.
const POINT_COLORS = ['#e74c3c', '#3498db', '#27ae60', '#f39c12', '#9b59b6', '#16a085']

export default {
  name: 'Aviris3Tab',
  props: {
    selectedBlock: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loadingScenes: false,
      scenesError: null,
      scenes: [],              // reflectance scenes covering the farm
      footprintsVisible: false,
      activeScene: null,       // the scene being sampled
      sampleLoading: false,
      sampleError: null,
      spectra: [],             // [{ label, color, lat, lon, scene, wavelengths, reflectance }]
      chartInstance: null,
      // RTK point-dataset workflow — its own folder-browser state so it
      // doesn't tangle with the Datasets drawer's shared navigation.
      avBreadcrumbs: [{ id: 'root', name: 'Root' }],
      avFolders: [],
      avDatasets: [],
      loadingFolder: false,
      folderError: null,
      selectedDataset: null,   // the dataset launched onto the map
      launchingDataset: false
    }
  },
  computed: {
    map () {
      return this.$store.state.mapObject
    },
    /**
     * Lon/lat bbox wrapping every block on the farm — the area we ask
     * the AVIRIS service for scene coverage over. Null when the farm
     * has no block geometry yet.
     */
    farmBbox () {
      const fc = this.$store.state.blocksGeoJson
      if (!fc || !fc.features || !fc.features.length) return null
      try {
        return turf.bbox(fc) // [minLon, minLat, maxLon, maxLat]
      } catch (e) {
        return null
      }
    }
  },
  created () {
    // Stable handler reference so we can map.off() it cleanly. Clicking
    // an RTK dataset point samples that point's exact survey coordinate;
    // clicking anywhere else freehand-samples at the click location.
    this._onMapClick = (e) => {
      if (!this.activeScene || this.sampleLoading) return
      const m = this.map
      if (m && m.getLayer(DATASET_LAYER)) {
        const hits = m.queryRenderedFeatures(e.point, { layers: [DATASET_LAYER] })
        const f = hits[0]
        if (f && f.geometry && f.geometry.coordinates) {
          this.sampleSpectrum(
            f.geometry.coordinates[1],
            f.geometry.coordinates[0],
            this.classLabelFor(f)
          )
          return
        }
      }
      this.sampleSpectrum(e.lngLat.lat, e.lngLat.lng)
    }
  },
  mounted () {
    this.loadScenes()
    this.queryFolder('root')
  },
  beforeDestroy () {
    // Tab teardown (switching tabs or closing the drawer) must not leave
    // the coverage overlay, sample markers, click handler, or crosshair
    // cursor stranded on the shared map. The launched dataset is left in
    // place — it's a normal platform layer, same as opening it from the
    // Datasets drawer.
    this.stopSampling()
    this.removeFootprints()
    this.removeSampleMarkers()
    this.destroyChart()
  },
  methods: {
    sceneDate (filename) {
      // AV320250809t153539_008_rfl.nc -> "Aug 9, 2025"
      const m = filename.match(/^AV3(\d{4})(\d{2})(\d{2})t/)
      if (!m) return ''
      const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
      })
    },
    // Map-label text: date plus resolution when the scene is indexed.
    sceneLabel (s) {
      const d = this.sceneDate(s.filename)
      return s.pixel_size_m ? `${d} · ${s.pixel_size_m} m` : d
    },
    // Scene-list detail line: resolution, grid size, band count — only
    // populated for indexed scenes (the others show date + filename).
    sceneDetail (s) {
      const parts = []
      if (s.pixel_size_m) parts.push(`${s.pixel_size_m} m/px`)
      if (s.grid && s.grid.width && s.grid.height) {
        parts.push(`${s.grid.width} × ${s.grid.height}`)
      }
      if (s.band_count) parts.push(`${s.band_count} bands`)
      return parts.join('  ·  ')
    },
    /**
     * fetch() with the caller's Firebase ID token attached. The AVIRIS
     * service verifies it and checks the farm's features.aviris flag —
     * same Bearer-token pattern the app uses for its Cloud Functions.
     */
    async authedFetch (url) {
      const user = firebase.auth().currentUser
      if (!user) throw new Error('Sign in required.')
      const token = await user.getIdToken()
      return fetch(url, { headers: { Authorization: 'Bearer ' + token } })
    },
    farmId () {
      return this.$store.state.farm && this.$store.state.farm.id
    },
    async loadScenes () {
      const bbox = this.farmBbox
      const farmId = this.farmId()
      if (!bbox || !farmId) return
      this.loadingScenes = true
      this.scenesError = null
      try {
        const r = await this.authedFetch(
          `${AVIRIS_BASE}/aviris/scenes?bbox=${bbox.join(',')}&farmId=${farmId}`
        )
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        // Reflectance scenes only — the _unc uncertainty companions
        // aren't separately useful in the picker.
        this.scenes = (data.scenes || [])
          .filter(s => s.kind === 'reflectance')
          .sort((a, b) => a.filename.localeCompare(b.filename))
        if (this.footprintsVisible) this.drawFootprints()
      } catch (err) {
        this.scenesError = 'Could not load AVIRIS-3 coverage: ' +
          (err.message || err)
      } finally {
        this.loadingScenes = false
      }
    },

    // --- footprint coverage overlay ---------------------------------
    toggleFootprints () {
      if (this.footprintsVisible) {
        this.removeFootprints()
      } else {
        this.drawFootprints()
      }
    },
    /**
     * Footprint FeatureCollection. Each feature carries its scene date
     * as `label` (drawn on the map) and an `active` flag so the layers
     * can emphasise whichever scene is currently selected for sampling.
     */
    footprintFC () {
      const activeFile = this.activeScene && this.activeScene.filename
      return {
        type: 'FeatureCollection',
        features: this.scenes
          .filter(s => s.geometry)
          .map(s => ({
            type: 'Feature',
            geometry: s.geometry,
            properties: {
              filename: s.filename,
              label: this.sceneLabel(s),
              active: s.filename === activeFile
            }
          }))
      }
    },
    drawFootprints () {
      const m = this.map
      if (!m || !this.scenes.length) return
      this.removeFootprints()
      m.addSource(FOOTPRINT_SRC, { type: 'geojson', data: this.footprintFC() })
      m.addLayer({
        id: FOOTPRINT_FILL,
        type: 'fill',
        source: FOOTPRINT_SRC,
        paint: {
          'fill-color': '#1abc9c',
          'fill-opacity': ['case', ['boolean', ['get', 'active'], false], 0.18, 0.08]
        }
      })
      // Soft white halo under the selected scene's outline only — makes
      // the highlight read clearly on top of satellite imagery, where a
      // plain coloured line would otherwise get lost.
      m.addLayer({
        id: FOOTPRINT_GLOW,
        type: 'line',
        source: FOOTPRINT_SRC,
        filter: ['boolean', ['get', 'active'], false],
        paint: {
          'line-color': '#ffffff',
          'line-width': 11,
          'line-opacity': 0.55,
          'line-blur': 3
        }
      })
      m.addLayer({
        id: FOOTPRINT_LINE,
        type: 'line',
        source: FOOTPRINT_SRC,
        paint: {
          // Bright amber for the selected scene — the app's highlight
          // colour. Teal blends into satellite imagery; amber pops.
          'line-color': ['case', ['boolean', ['get', 'active'], false], '#ffc107', '#1abc9c'],
          'line-width': ['case', ['boolean', ['get', 'active'], false], 4, 2],
          'line-opacity': ['case', ['boolean', ['get', 'active'], false], 1, 0.8]
        }
      })
      m.addLayer({
        id: FOOTPRINT_LABEL,
        type: 'symbol',
        source: FOOTPRINT_SRC,
        layout: {
          'text-field': ['get', 'label'],
          'text-size': ['case', ['boolean', ['get', 'active'], false], 14, 11],
          // The selected scene's label always shows; the rest collide
          // away normally (CLEREL's footprints overlap heavily).
          'text-allow-overlap': ['boolean', ['get', 'active'], false]
        },
        paint: {
          'text-color': ['case', ['boolean', ['get', 'active'], false], '#7a5200', '#0e6b59'],
          'text-halo-color': '#ffffff',
          'text-halo-width': ['case', ['boolean', ['get', 'active'], false], 2.2, 1.5]
        }
      })
      this.footprintsVisible = true
    },
    refreshFootprintData () {
      // Update the active-scene emphasis without re-adding the layers.
      const m = this.map
      if (!m) return
      const src = m.getSource(FOOTPRINT_SRC)
      if (src) src.setData(this.footprintFC())
    },
    removeFootprints () {
      const m = this.map
      if (!m) return
      if (m.getLayer(FOOTPRINT_LABEL)) m.removeLayer(FOOTPRINT_LABEL)
      if (m.getLayer(FOOTPRINT_LINE)) m.removeLayer(FOOTPRINT_LINE)
      if (m.getLayer(FOOTPRINT_GLOW)) m.removeLayer(FOOTPRINT_GLOW)
      if (m.getLayer(FOOTPRINT_FILL)) m.removeLayer(FOOTPRINT_FILL)
      if (m.getSource(FOOTPRINT_SRC)) m.removeSource(FOOTPRINT_SRC)
      this.footprintsVisible = false
    },

    // --- RTK point dataset: folder browser --------------------------
    /**
     * Query one folder's child folders + datasets. The AVIRIS tab keeps
     * its own browse state rather than driving the Datasets drawer's
     * shared state, so the two never fight over the current folder.
     */
    queryFolder (folderId) {
      const farmId = this.farmId()
      if (!farmId) return
      this.loadingFolder = true
      this.folderError = null
      Promise.all([
        fb.foldersCollection
          .where('farmId', '==', farmId)
          .where('folderId', '==', folderId)
          .orderBy('name', 'asc').get(),
        fb.datasetsCollection
          .where('farmId', '==', farmId)
          .where('folderId', '==', folderId)
          .orderBy('updatedOn', 'desc').get()
      ])
        .then(([folderSnap, datasetSnap]) => {
          this.avFolders = folderSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          this.avDatasets = datasetSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        })
        .catch(err => {
          this.folderError = 'Could not browse folder: ' + (err.message || err)
        })
        .finally(() => { this.loadingFolder = false })
    },
    browseFolder (folder) {
      this.avBreadcrumbs.push({ id: folder.id, name: folder.name })
      this.queryFolder(folder.id)
    },
    crumbTo (crumb, index) {
      this.avBreadcrumbs = this.avBreadcrumbs.slice(0, index + 1)
      this.queryFolder(crumb.id)
    },
    selectDataset (dataset) {
      this.selectedDataset = dataset
      this.launchingDataset = true
      // launchDataset renders the dataset on the shared map with its own
      // saved vizSettings — identical to opening it from the Datasets
      // drawer — so it looks exactly as the researcher configured it.
      Promise.resolve(this.$store.dispatch('launchDataset', { dataset }))
        .catch(err => {
          this.folderError = 'Could not load dataset: ' + (err.message || err)
        })
        .finally(() => { this.launchingDataset = false })
    },
    /**
     * The class value for a clicked dataset point — read from whichever
     * header the dataset is configured to colour by. Used to label the
     * sampled spectrum so curves group by class in the chart.
     */
    classLabelFor (feature) {
      const ds = this.selectedDataset
      let header = ds && ds.vizSettings && ds.vizSettings.colorHeader
      if (header && typeof header === 'object') {
        header = header.machine_name || header.label || null
      }
      if (!header || !feature.properties) return null
      const v = feature.properties[header]
      return (v === null || v === undefined || v === '') ? null : v
    },

    // --- spectrum sampling ------------------------------------------
    selectScene (scene) {
      this.activeScene = scene
      this.sampleError = null
      // Flag the store so the farm-map's blocksLayer handler AND the
      // dataset onClickFunction both bail — without this, a map click
      // would also navigate to a block or open the datapoint drawer.
      this.$store.state.avirisSampling = true
      const m = this.map
      if (m) {
        m.on('click', this._onMapClick)
        m.getCanvas().style.cursor = 'crosshair'
      }
      // Show coverage so the user can see which scene they picked, with
      // the active one emphasised + always-labelled.
      if (this.footprintsVisible) {
        this.refreshFootprintData()
      } else {
        this.drawFootprints()
      }
    },
    stopSampling () {
      const m = this.map
      if (m) {
        m.off('click', this._onMapClick)
        m.getCanvas().style.cursor = ''
      }
      this.$store.state.avirisSampling = false
      this.activeScene = null
      this.refreshFootprintData()
    },
    async sampleSpectrum (lat, lon, classLabel) {
      const scene = this.activeScene
      if (!scene) {
        this.sampleError = 'Pick a scene above before sampling points.'
        return
      }
      const farmId = this.farmId()
      if (!farmId) return
      this.sampleLoading = true
      this.sampleError = null
      try {
        const url = `${AVIRIS_BASE}/aviris/spectrum` +
          `?scene=${encodeURIComponent(scene.filename)}` +
          `&lat=${lat}&lon=${lon}&farmId=${farmId}`
        const r = await this.authedFetch(url)
        if (r.status === 422) {
          throw new Error('That point isn’t inside the selected scene.')
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        if (!data.valid) {
          this.sampleError =
            'No reflectance there — the point falls in the scene’s no-data margin.'
          return
        }
        const i = this.spectra.length
        const base = `Point ${i + 1}`
        const hasClass = classLabel !== null && classLabel !== undefined && classLabel !== ''
        this.spectra.push({
          label: hasClass ? `${classLabel} · ${base}` : base,
          color: POINT_COLORS[i % POINT_COLORS.length],
          lat,
          lon,
          scene: scene.filename,
          wavelengths: data.wavelengths_nm,
          reflectance: data.reflectance
        })
        this.updateSampleMarkers()
        this.$nextTick(() => this.renderChart())
      } catch (err) {
        this.sampleError = err.message || String(err)
      } finally {
        this.sampleLoading = false
      }
    },
    clearSpectra () {
      this.spectra = []
      this.sampleError = null
      this.removeSampleMarkers()
      this.destroyChart()
    },

    // --- sample-point markers ---------------------------------------
    updateSampleMarkers () {
      const m = this.map
      if (!m) return
      const fc = {
        type: 'FeatureCollection',
        features: this.spectra.map(s => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
          properties: { color: s.color, label: s.label }
        }))
      }
      const src = m.getSource(SAMPLE_SRC)
      if (src) {
        src.setData(fc)
        return
      }
      m.addSource(SAMPLE_SRC, { type: 'geojson', data: fc })
      m.addLayer({
        id: SAMPLE_LAYER,
        type: 'circle',
        source: SAMPLE_SRC,
        paint: {
          'circle-radius': 6,
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      })
    },
    removeSampleMarkers () {
      const m = this.map
      if (!m) return
      if (m.getLayer(SAMPLE_LAYER)) m.removeLayer(SAMPLE_LAYER)
      if (m.getSource(SAMPLE_SRC)) m.removeSource(SAMPLE_SRC)
    },

    // --- spectrum chart ---------------------------------------------
    renderChart () {
      const ChartCtor = this.$Chart || window.Chart
      const canvas = this.$refs.spectrumCanvas
      if (!ChartCtor || !canvas || !this.spectra.length) return

      const datasets = this.spectra.map(s => ({
        label: s.label,
        // {x: wavelength, y: reflectance} pairs so the x-axis is a true
        // linear nm scale rather than 284 category ticks. null bands
        // (atmospheric water-absorption regions) gap the line.
        data: s.wavelengths.map((w, i) => ({ x: w, y: s.reflectance[i] })),
        borderColor: s.color,
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        pointRadius: 0,
        spanGaps: false,
        tension: 0
      }))

      if (this.chartInstance) {
        this.chartInstance.data.datasets = datasets
        this.chartInstance.update()
        return
      }

      this.chartInstance = new ChartCtor(canvas.getContext('2d'), {
        type: 'line',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: 'nearest', intersect: false },
          plugins: {
            legend: { display: true, position: 'top', labels: { boxWidth: 10 } }
          },
          scales: {
            x: {
              type: 'linear',
              title: { display: true, text: 'Wavelength (nm)' },
              ticks: { maxTicksLimit: 8 }
            },
            y: {
              title: { display: true, text: 'Reflectance' }
            }
          }
        }
      })
    },
    destroyChart () {
      if (this.chartInstance) {
        try { this.chartInstance.destroy() } catch (e) { /* ignore */ }
        this.chartInstance = null
      }
    }
  }
}
</script>

<style scoped>
.aviris-section-label {
  font-size: 0.78rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.aviris-scene-list {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  overflow: hidden;
}
.aviris-scene {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-bottom: 1px solid #eef1f4;
  cursor: pointer;
}
.aviris-scene:last-child { border-bottom: none; }
.aviris-scene:hover { background: #f7f9fa; }
.aviris-scene.is-active {
  background: rgba(26, 188, 156, 0.10);
  box-shadow: inset 3px 0 0 #1abc9c;
}
.aviris-scene-main { min-width: 0; }
.aviris-scene-date {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2c3e50;
}
.aviris-scene-detail {
  display: block;
  font-size: 0.74rem;
  font-weight: 600;
  color: #14866f;
}
.aviris-scene-file {
  display: block;
  font-size: 0.7rem;
  color: #95a5a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aviris-badge {
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f0f1f3;
  color: #95a5a6;
}
.aviris-badge.is-ready {
  background: rgba(26, 188, 156, 0.14);
  color: #14866f;
}

.aviris-dataset {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  padding: 8px 10px;
}
.aviris-crumbs {
  font-size: 0.78rem;
  margin-bottom: 6px;
}
.aviris-crumb {
  color: #1abc9c;
  cursor: pointer;
}
.aviris-crumb:hover { text-decoration: underline; }
.aviris-crumb-sep { color: #b0b7bd; }
.aviris-browse {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}
.aviris-browse-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px solid #eef1f4;
  cursor: pointer;
  font-size: 0.85rem;
  color: #2c3e50;
}
.aviris-browse-row:last-child { border-bottom: none; }
.aviris-browse-row:hover { background: #f7f9fa; }
.aviris-browse-dataset.is-active {
  background: rgba(26, 188, 156, 0.10);
  box-shadow: inset 3px 0 0 #1abc9c;
}
.aviris-browse-icon { color: #95a5a6; width: 16px; text-align: center; }
.aviris-browse-name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aviris-hint {
  margin: 6px 0 0;
  font-size: 0.76rem;
  color: #6c757d;
}

.aviris-sampling {
  background: rgba(26, 188, 156, 0.10);
  border: 1px solid rgba(26, 188, 156, 0.35);
  border-radius: 6px;
  padding: 7px 10px;
}
.aviris-sampling-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: #14866f;
  font-weight: 600;
}
.aviris-sample-error {
  margin-top: 5px;
  font-size: 0.78rem;
  color: #c0392b;
}

.aviris-chart-panel {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  padding: 8px 10px;
}
.aviris-chart-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.78rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}
.aviris-chart-canvas {
  position: relative;
  height: 200px;
}
.aviris-point-list { margin-top: 6px; }
.aviris-point {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  padding: 2px 0;
}
.aviris-point-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.aviris-point-label { font-weight: 600; color: #2c3e50; }
.aviris-point-coord { color: #95a5a6; font-variant-numeric: tabular-nums; }

.btn-xs {
  font-size: 0.7rem;
  padding: 1px 8px;
  line-height: 1.5;
}
</style>
